import React from 'react';
import { Calendar, Clock, Phone, MapPin, ExternalLink, Star, CheckCircle, Users, Facebook, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const PLANITY_URL = 'https://www.planity.com/hilanta-13004-marseille';
const FACEBOOK_URL = 'https://www.facebook.com/hilanta.marseille';
const INSTAGRAM_URL = 'https://www.instagram.com/espace_hilanta/';

const ReservationSimple = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-sand-light" data-testid="reservation-page">
      <title>Réservation | Hilanta - Massages & Bien-être Marseille</title>
      <meta name="description" content="Réservez votre massage chez Hilanta à Marseille. Réservation sur Planity, paiement sur place." />
      
      {/* Header */}
      <section className="py-8 sm:py-12 bg-sand">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calendar className="mx-auto text-terracotta mb-4" size={40} />
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-warm-brown mb-4">
            Réservez votre soin
          </h1>
          <p className="font-body text-sm sm:text-base text-warm-brown-light max-w-2xl mx-auto">
            Réservez en ligne et payez sur place le jour de votre rendez-vous
          </p>
        </div>
      </section>

      {/* Main CTA - Planity — redesigned for maximum impact */}
      <section className="py-8 sm:py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ border: '1px solid rgba(196,120,90,0.2)' }}>

            {/* Header — image + gradient overlay */}
            <div className="relative overflow-hidden" style={{ minHeight: '220px' }}>
              <img
                src="https://images.pexels.com/photos/3230236/pexels-photo-3230236.jpeg?auto=compress&cs=tinysrgb&w=900"
                alt="Soin bien-être Hilanta"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: 'center 40%' }}
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(180,82,56,0.88) 0%, rgba(140,60,42,0.82) 50%, rgba(74,66,56,0.72) 100%)' }} />
              
              {/* Botanical leaf decorations */}
              <div className="absolute top-0 right-0 w-28 h-28 opacity-15 pointer-events-none">
                <svg viewBox="0 0 112 112" xmlns="http://www.w3.org/2000/svg">
                  <path d="M112 5 Q90 30 65 60 Q42 88 20 110" stroke="white" strokeWidth="1.5" fill="none"/>
                  <ellipse cx="98" cy="22" rx="16" ry="5.5" fill="white" transform="rotate(48 98 22)" opacity=".6"/>
                  <ellipse cx="80" cy="44" rx="14" ry="5" fill="white" transform="rotate(44 80 44)" opacity=".6"/>
                  <ellipse cx="60" cy="70" rx="15" ry="5.5" fill="white" transform="rotate(42 60 70)" opacity=".5"/>
                </svg>
              </div>
              <div className="absolute bottom-0 left-0 w-24 h-24 opacity-10 pointer-events-none">
                <svg viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 92 Q28 65 55 38 Q78 15 94 2" stroke="white" strokeWidth="1.5" fill="none"/>
                  <ellipse cx="18" cy="78" rx="14" ry="5" fill="white" transform="rotate(-48 18 78)" opacity=".6"/>
                  <ellipse cx="38" cy="58" rx="13" ry="4.5" fill="white" transform="rotate(-45 38 58)" opacity=".5"/>
                </svg>
              </div>

              {/* Content */}
              <div className="relative z-10 p-6 sm:p-8 text-center text-white">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-5 text-xs font-medium tracking-wide">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  Réservation 24h/24 — Ouvert Mar–Sam 10h–18h
                </div>

                <Calendar className="mx-auto mb-3 text-white/90" size={42} />
                <h2 className="font-heading text-3xl sm:text-4xl mb-2 text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                  Réservation en ligne
                </h2>
                <p className="text-white/85 text-sm sm:text-base font-body">
                  Choisissez votre soin et votre créneau sur Planity
                </p>

                {/* Stars */}
                <div className="flex items-center justify-center gap-1 mt-3">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} size={16} className="text-amber-300 fill-amber-300" />
                  ))}
                  <span className="text-white/80 text-xs ml-1 font-body">40+ avis 5★</span>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="bg-white px-6 sm:px-8 pt-7 pb-8">

              {/* Benefits in a 2×2 grid */}
              <div className="grid grid-cols-2 gap-3 mb-7">
                {[
                  { icon: '💳', label: 'Paiement sur place', sub: 'le jour du RDV' },
                  { icon: '💬', label: 'Rappel SMS', sub: 'avant votre RDV' },
                  { icon: '✏️', label: 'Modification facile', sub: 'de votre RDV' },
                  { icon: '⏰', label: 'Disponible 24h/24', sub: 'réservez quand vous voulez' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3 p-3 rounded-2xl" style={{ background: '#FBF5F2' }}>
                    <span className="text-xl flex-shrink-0 mt-0.5">{item.icon}</span>
                    <div>
                      <p className="font-semibold text-warm-brown text-sm leading-tight">{item.label}</p>
                      <p className="text-warm-brown-light text-xs mt-0.5">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Main CTA button — animated, eye-catching */}
              <a
                href={PLANITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <button
                  data-testid="planity-btn"
                  className="relative w-full text-white font-body font-semibold text-lg rounded-2xl py-5 overflow-hidden transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    background: 'linear-gradient(135deg, #C4785A 0%, #A85A40 50%, #C4785A 100%)',
                    backgroundSize: '200% 200%',
                    boxShadow: '0 8px 30px rgba(196,120,90,0.45), 0 2px 8px rgba(196,120,90,0.3)',
                  }}
                >
                  {/* Shimmer effect */}
                  <span className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
                    <span className="absolute top-0 -left-full h-full w-1/2 bg-white/15 skew-x-12 animate-[shimmer_2.5s_infinite]" style={{ animation: 'shimmer 2.5s infinite' }} />
                  </span>
                  <span className="relative flex items-center justify-center gap-3">
                    <Calendar size={22} />
                    <span>Réserver sur Planity</span>
                    <ExternalLink size={18} className="opacity-80" />
                  </span>
                </button>
              </a>

              {/* Reassurance line */}
              <p className="text-center text-warm-brown-light text-xs mt-3 font-body flex items-center justify-center gap-1.5">
                <CheckCircle size={13} className="text-green-500" />
                Confirmation immédiate · Gratuit · Paiement sur place
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Info Cards */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-md text-center">
              <CheckCircle className="mx-auto text-green-500 mb-2" size={28} />
              <p className="text-sm text-warm-brown font-medium">Confirmation immédiate</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md text-center">
              <Clock className="mx-auto text-terracotta mb-2" size={28} />
              <p className="text-sm text-warm-brown font-medium">Réservation 24h/24</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md text-center">
              <Phone className="mx-auto text-sage mb-2" size={28} />
              <p className="text-sm text-warm-brown font-medium">Rappel SMS</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md text-center">
              <Star className="mx-auto text-amber-500 mb-2" size={28} />
              <p className="text-sm text-warm-brown font-medium">40+ avis 5★</p>
            </div>
          </div>
        </div>
      </section>

      {/* Activités & Conférences */}
      <section className="py-8 sm:py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Users className="mx-auto text-blue-600 mb-4" size={36} />
            <h2 className="font-heading text-2xl sm:text-3xl text-warm-brown mb-2">
              Activités & Conférences
            </h2>
            <p className="font-body text-warm-brown-light text-sm">
              Inscription sur place ou via nos réseaux sociaux
            </p>
          </div>
          
          <div className="bg-blue-50 rounded-3xl p-6 md:p-8">
            {/* Activities list */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 border border-blue-100">
                <h3 className="font-heading text-lg text-warm-brown mb-1">Activité créative</h3>
                <p className="font-body text-sm text-warm-brown-light mb-2">Enfants - Places limitées</p>
                <span className="font-heading text-xl text-blue-600">25 €</span>
              </div>
              <div className="bg-white rounded-xl p-4 border border-blue-100">
                <h3 className="font-heading text-lg text-warm-brown mb-1">Conférence santé naturelle</h3>
                <p className="font-body text-sm text-warm-brown-light mb-2">Adultes - Places limitées</p>
                <span className="font-heading text-xl text-blue-600">20 €</span>
              </div>
            </div>
            
            {/* How to register */}
            <div className="text-center">
              <p className="font-body text-warm-brown mb-4">
                Découvrez les dates et les thèmes sur nos pages Facebook et Instagram !
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a 
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white rounded-full px-6 py-3"
                  >
                    <Facebook className="mr-2" size={18} />
                    Facebook
                  </Button>
                </a>
                
                <a 
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    variant="outline"
                    className="border-pink-600 text-pink-600 hover:bg-pink-600 hover:text-white rounded-full px-6 py-3"
                  >
                    <Instagram className="mr-2" size={18} />
                    Instagram
                  </Button>
                </a>
                
                <span className="text-warm-brown-light font-body text-sm">ou</span>
                
                <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 border border-sand">
                  <MapPin className="text-terracotta" size={18} />
                  <span className="font-body text-warm-brown">Sur place</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gift Card CTA */}
      <section className="py-8 sm:py-12 bg-sand/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl text-warm-brown mb-4">
            Vous souhaitez offrir un soin ?
          </h2>
          <p className="font-body text-warm-brown-light mb-6">
            Achetez une carte cadeau en ligne et offrez un moment de bien-être
          </p>
          <Link to="/carte-cadeau">
            <Button className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-4 text-lg">
              🎁 Offrir une carte cadeau
            </Button>
          </Link>
        </div>
      </section>

      {/* Contact */}
      <section className="py-8 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-body text-warm-brown-light text-sm mb-2">
            Besoin d'aide pour réserver ? Appelez-nous
          </p>
          <a href="tel:+33960421822" className="text-terracotta font-heading text-2xl hover:underline">
            09 60 42 18 22
          </a>
          <div className="mt-4 flex items-center justify-center gap-2 text-warm-brown-light">
            <MapPin size={16} />
            <span className="font-body text-sm">58 Avenue Maréchal Foch, 13004 Marseille</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReservationSimple;
